import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cashPayment, manualPayment, orderDetails } from "../../../../services/orderService";
import { Loader2, CreditCard, Users, Calendar, Split, Star, AlertCircle, Banknote, NotebookPen } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmationPopup from "../../../../components/common/ConfirmationPopup";
import { useBistroNavigation } from "../../../../hooks/useBistroNavigation";
import Textarea from "../../../../components/form/Textarea";
import Input from "../../../../components/form/Input";

const OrderDetails = ({ orderId }) => {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [cashError, setCashError] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [paymentType, setPaymentType] = useState("manual");
  const { getPath, config } = useBistroNavigation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["orderDetails", orderId],
    queryFn: () => orderDetails({ id: orderId }),
    enabled: !!orderId,
  });

  const { mutate: manualPaymentHandler, isPending: manualPayLoading } = useMutation({
    mutationFn: async () => {
      const res = await manualPayment({ orderId: order?.orderId, ...(notes !== "" && { note: notes }) });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Manual payment recorded successfully!");
        queryClient.invalidateQueries(["orderDetails", orderId]);
        queryClient.invalidateQueries(["order"]);
      } else {
        toast.warning(res?.message || "Something went wrong!");
      }
    },
    onError: (err) => {
      toast.error("Failed to record manual payment!");
      console.error(err);
    },
  });

  const { mutate: cashPaymentHandler, isPending: cashPayLoading } = useMutation({
    mutationFn: async () => {
      const res = await cashPayment({ orderId: order.orderId, amount: Number(cashAmount), note: notes });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Cash payment recorded successfully!");
        queryClient.invalidateQueries(["orderDetails", orderId]);
        queryClient.invalidateQueries(["order"]);
      } else {
        toast.warning(res?.message || "Something went wrong!");
      }
    },
    onError: (err) => {
      toast.error("Failed to record manual payment!");
      console.error(err);
    },
  });

  const handleConfirmAction = (action) => {
    if (action.button_type === "confirm") {
      if (paymentType === "cash") {
        const numericCashAmount = parseFloat(cashAmount);

        if (!cashAmount || !numericCashAmount || numericCashAmount <= 0) {
          setCashError("Cash amount is required.");
          return;
        }

        setCashError("");
        cashPaymentHandler();
      } else {
        manualPaymentHandler();
      }
    }
    setConfirmOpen(false);
  };

  const NotesSection = React.useCallback(
    ({ notes, setNotes }) => (
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all w-full max-w-md">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <i class="fa-regular fa-pen-to-square"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">Additional Notes</h3>
            <p className="text-xs text-gray-500 mt-0.5">Add any special instructions or comments</p>
          </div>
        </div>
        <div className="relative">
          <Textarea
            inputPlaceholder="Type your notes here..."
            inputClasses="!w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all placeholder:text-gray-400 resize-none min-h-[120px]"
            inputName="notes"
            inputValue={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-white px-2 py-1 rounded-md shadow-sm">{notes.length} / 500</div>
        </div>

        {notes.length > 0 ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            <i className="fa-solid fa-check-circle"></i>
            <span>Note saved</span>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <i className="fa-regular fa-lightbulb"></i>
            <span>Optional: Add delivery instructions, dietary preferences, etc.</span>
          </div>
        )}
      </div>
    ),
    []
  );

  const CashPaymentSection = React.useCallback(
    ({ notes, setNotes, cashAmount, setCashAmount, totalAmount, cashError }) => {
      const numericCashAmount = parseFloat(cashAmount) || 0;
      const isValidAmount = numericCashAmount > 0;
      const change = numericCashAmount > totalAmount ? numericCashAmount - totalAmount : 0;
      return (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Banknote className="text-green-600" size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">Cash Payment Details</h3>
              <p className="text-xs text-gray-500">Enter cash amount and notes</p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
              <i className="fa-solid fa-dollar-sign text-green-600 text-sm"></i>
              Cash Amount Received
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</div>
              <Input
                inputType="text"
                inputMode="decimal"
                inputPlaceholder="0.00"
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^\d.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts.slice(1).join("");
                  }
                  if (parts.length === 2 && parts[1].length > 2) {
                    value = parts[0] + "." + parts[1].substring(0, 2);
                  }
                  setCashAmount(value);
                }}
                inputName="cashAmount"
                value={cashAmount}
                inputClasses={`!w-full border-2 rounded-lg pl-8 pr-3.5 py-2.5 text-sm font-semibold bg-white transition-all ${
                  isValidAmount
                    ? "border-green-300 focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
                    : "border-gray-300 focus:ring-2 focus:ring-gray-300 focus:border-gray-400"
                }`}
                autoComplete="off"
              />
              {cashError && (
                <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2.5 py-1.5 rounded-md mt-1">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <span>{cashError}</span>
                </div>
              )}
            </div>

            {cashAmount && !isValidAmount && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1.5 rounded-md">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <span>Please enter a valid amount</span>
              </div>
            )}

            {isValidAmount && (
              <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1.5 rounded-md">
                <i className="fa-solid fa-check-circle"></i>
                <span>Valid amount entered: ${numericCashAmount.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* {totalAmount && isValidAmount && (
            <div className="bg-white border border-green-200 rounded-lg p-3 mb-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 font-medium">Bill Total:</span>
                <span className="text-gray-900 font-semibold">${totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 font-medium">Cash Received:</span>
                <span className="text-green-600 font-semibold">${numericCashAmount.toFixed(2)}</span>
              </div>
              {change > 0 && (
                <>
                  <div className="border-t border-gray-200 pt-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700 font-semibold flex items-center gap-1.5">
                      <i className="fa-solid fa-exchange-alt text-blue-600"></i>
                      Change to Return:
                    </span>
                    <span className="text-lg text-blue-600 font-bold">${change.toFixed(2)}</span>
                  </div>
                </>
              )}
              {numericCashAmount < totalAmount && (
                <div className="bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-2 mt-2">
                  <i className="fa-solid fa-exclamation-circle text-red-500 text-sm mt-0.5"></i>
                  <p className="text-xs text-red-700">Insufficient amount. Need ${(totalAmount - numericCashAmount).toFixed(2)} more.</p>
                </div>
              )}
            </div>
          )} */}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
              <i className="fa-solid fa-note-sticky text-gray-600 text-sm"></i>
              Payment Notes
              <span className="text-gray-400 font-normal lowercase text-xs">(optional)</span>
            </label>
            <Textarea
              inputPlaceholder="Add notes about this payment…"
              inputClasses="!w-full border-2 border-green-200 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all placeholder:text-gray-400 resize-none min-h-[80px]"
              inputName="notes"
              inputValue={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {notes && (
              <div className="flex items-center gap-1.5 text-xs text-green-600">
                <i className="fa-solid fa-check-circle"></i>
                <span>{notes.length} characters</span>
              </div>
            )}
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <i className="fa-solid fa-info-circle text-blue-600 text-sm mt-0.5"></i>
            <p className="text-xs text-blue-700">
              <strong>Tip:</strong> Enter the exact cash amount received from the customer. The system will calculate change automatically.
            </p>
          </div>
        </div>
      );
    },
    [setNotes, setCashAmount]
  );

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-60">
        <Loader2 className="animate-spin text-gray-500" size={28} />
      </div>
    );

  if (isError || !data?.data) return <div className="text-center text-red-500 py-10">Failed to load order details.</div>;

  const order = data.data;

  const feedbackMap = {};
  order.transactionDetails?.forEach((tx) => {
    feedbackMap[tx._id] = { ...tx };
  });

  const typeColors = {
    full: "bg-green-100 text-green-700",
    cash_amount: "bg-yellow-100 text-yellow-700",
    custom_amount: "bg-purple-100 text-purple-700",
    split_amount: "bg-blue-100 text-blue-700",
    split_items: "bg-pink-100 text-pink-700",
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <img src={config?.logo || "/placeholder.png"} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
          <div>
            <h2 className="text-lg font-semibold capitalize">{order.organizationData?.name}</h2>
            <p className="text-sm text-gray-500">Order ID: {order.orderId}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {order.status === "pending" || order.status === "partial" ? (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setConfirmOpen(true);
                  setPaymentType("manual");
                  setCashAmount("");
                  setNotes("");
                }}
                disabled={loading}
                className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90 transition text-sm"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {loading ? "Processing..." : `Manual Payment ($${order?.remainingAmount.toFixed(2)})`}
              </button>
              <button
                onClick={() => {
                  setConfirmOpen(true);
                  setPaymentType("cash");
                  setCashAmount("");
                  setNotes("");
                }}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition text-sm"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}
                {loading ? "Processing..." : `Record Cash Payment`}
              </button>
            </div>
          ) : (
            <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-md font-medium">Payment Completed</span>
          )}

          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              order.status === "paid"
                ? "bg-green-100 text-green-700"
                : order.status === "partial"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {order.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Table</p>
          <p className="font-medium">
            {order.tableData?.tableName} (#{order.tableData?.tableNumber})
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Persons</p>
          <p className="font-medium flex items-center gap-1">
            <Users size={14} /> {order.numberOfPerson}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-500">Created</p>
          <p className="font-medium flex items-center gap-1">
            <Calendar size={14} /> {new Date(order.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-800 mb-2">Ordered Items</h3>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left">Product</th>
                <th className="px-4 py-2 text-left">Variant</th>
                <th className="px-4 py-2 text-center">Qty</th>
                <th className="px-4 py-2 text-right">Price</th>
                <th className="px-4 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-2">{item.productName}</td>
                  <td className="px-4 py-2">{item.variantName}</td>
                  <td className="px-4 py-2 text-center">{item.qty}</td>
                  <td className="px-4 py-2 text-right">${item.price}</td>
                  <td className="px-4 py-2 text-right">${item.subtotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <CreditCard size={18} /> Payment Summary
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <p className="text-gray-600">Subtotal:</p>
          <p className="text-right font-medium">${order.subtotal}</p>
          <p className="text-gray-600">Bill Amount:</p>
          <p className="text-right font-medium">${order.billAmount}</p>
          <p className="text-gray-600">Total Paid:</p>
          <p className="text-right font-semibold text-green-700">${order.payableAmount.toFixed(2)}</p>
        </div>
        {order.status === "partial" && (
          <div className="flex items-center text-yellow-700 text-sm mt-2">
            <AlertCircle size={16} className="mr-1" />
            <span>Partial payment pending</span>
          </div>
        )}
      </div>

      {order.payment?.splits?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <Split size={18} /> Split Payments
          </h3>

          {order.payment.splits.map((split, index) => {
            const feedback = feedbackMap[split.transactionId] || {};

            return (
              <div key={index} className="border rounded-lg p-3 mb-3 bg-gray-50 space-y-2">
                <div className="flex justify-between items-center text-sm text-gray-700">
                  <span>Split #{index + 1}</span>
                  <span className="font-semibold">${split.amount.toFixed(2)}</span>
                </div>

                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize inline-block w-fit ${
                    typeColors[split.splitType] || "bg-gray-100 text-gray-700"
                  }`}
                >
                  {split.splitType?.replace(/_/g, " ")}
                </span>

                {split.splitType === "split_item" && (
                  <table className="w-full text-xs border-t mt-2">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="py-1 text-left">Item</th>
                        <th className="py-1 text-center">Qty</th>
                        <th className="py-1 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {split.items.map((sItem, idx2) => (
                        <tr key={idx2} className="border-t">
                          <td className="py-1">{sItem.productName}</td>
                          <td className="py-1 text-center">{sItem.qty}</td>
                          <td className="py-1 text-right">${sItem.subtotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {feedback.rating > 0 || feedback.comment ? (
                  <div className="bg-white rounded-md p-2 border mt-2">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} className={`${i < feedback.rating ? "text-yellow-500 fill-yellow-400" : "text-gray-300"}`} />
                      ))}
                    </div>
                    {feedback.comment && <p className="text-xs text-gray-600 italic">“{feedback.comment}”</p>}
                  </div>
                ) : null}

                {(split?.splitType === "full" || split?.splitType === "cash_amount") && feedback.note !== null && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2 text-xs text-blue-700">
                    <div className="flex items-center gap-1 mb-1">
                      <i className="fa-solid fa-note-sticky text-blue-600"></i>
                      <span className="font-semibold">Payment Notes</span>
                    </div>
                    <p className="italic">"{feedback?.note}"</p>
                  </div>
                )}

                {split.splitType === "split_amount" && split.noOfPerson > 0 && (
                  <div className="bg-white border rounded-md p-3 mt-2 text-sm">
                    <p className="font-medium text-gray-700">
                      👥 This payment covers <span className="font-semibold">{split.noOfPerson}</span> {split.noOfPerson > 1 ? "people" : "person"}.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Based on the total bill, this share represents approximately{" "}
                      <span className="font-semibold">{((split.amount / order.billAmount) * 100).toFixed(1)}%</span> of the total payment.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {order.miniStatement?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Mini Statement</h3>
          <div className="space-y-2">
            {order.miniStatement.map((tx, index) => (
              <div key={index} className="flex justify-between bg-gray-50 p-3 rounded-lg text-sm">
                <div>
                  <p className="font-mono text-gray-700">{tx?.transactionId}</p>
                  <p className="text-xs text-gray-500">Tip: ${tx.tipAmount}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="font-semibold">${(tx.amount + tx.tipAmount).toFixed(2)}</span>

                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize
      ${
        tx.paymentMethod === "cash"
          ? "bg-green-100 text-green-700"
          : tx.paymentMethod === "card"
          ? "bg-blue-100 text-blue-700"
          : "bg-purple-100 text-purple-700"
      }`}
                  >
                    {tx.paymentMethod}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <ConfirmationPopup
        isOpen={confirmOpen}
        setIsOpen={setConfirmOpen}
        getActionValue={handleConfirmAction}
        title={paymentType === "cash" ? "Cash Payment" : "Manual Payment"}
        message={
          paymentType === "cash" ? (
            <CashPaymentSection
              notes={notes}
              setNotes={setNotes}
              cashAmount={cashAmount}
              setCashAmount={setCashAmount}
              totalAmount={order.remainingAmount}
              cashError={cashError}
            />
          ) : (
            <NotesSection notes={notes} setNotes={setNotes} />
          )
        }
        confirmButtonLabel="Pay"
        cancelButtonLabel="Cancel"
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default OrderDetails;
